import { Award, Ban, RotateCcw, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { PreviewTable } from '../../../../components/PreviewTable';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell, ProfileHeaderSkeleton } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { AwardModal } from '../components/AwardModal';
import { NomineesPanel } from '../components/NomineesPanel';
import { NomineeStagesPanel } from '../components/NomineeStagesPanel';
import { nominationsApi } from '../praiseApi';
import type { PraiseSurface } from '../praiseSurface';
import type { AwardDetail } from '../praiseTypes';

type TabKey = 'info' | 'criteria' | 'nominees';

/** What is open: one section's own edit, or a dialog that only confirms. One at a time. */
type Pending = 'details' | 'criteria' | 'delete' | 'restore' | null;

/**
 * One award, on the header layout, for BOTH surfaces — **Info · Nominees**.
 *
 * **It LANDS ON NOMINEES**, not Info (still the first declared): an award is a container opened for
 * its ROSTER — the registration of the employees nominated, and on Evaluation the list to judge.
 * Info carries the award's own fields (its own Edit, Nomination only), the checklist every nominee is
 * asked for (read off the category, edited there) and the Audit.
 */
export function AwardProfilePage({ surface }: { surface: PraiseSurface }) {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const writes = surface.writes;

  const [award, setAward] = useState<AwardDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('nominees');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      surface.api
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) {
            setAward(next);
            setError(null);
          }
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this award.', 0));
        });
    },
    [id, surface],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'PRAISE',
    { label: surface.label, to: surface.basePath },
    award?.categoryName ?? 'Award',
  ];

  // Guarded on having NOTHING to show — a failed refresh keeps the record and goes to the banner.
  if (error && !award) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!award) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ProfileHeaderSkeleton tabs={2} />
      </div>
    );
  }

  const record = award;

  async function runConfirmed() {
    setRunning(true);
    setActionError(null);

    try {
      if (pending === 'delete') await nominationsApi.remove(record.id);
      else await nominationsApi.restore(record.id);

      toast.success(`${record.categoryName} was ${pending === 'delete' ? 'deleted' : 'restored'}.`);
      setPending(null);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  const documents = `${record.checklist.length} ${record.checklist.length === 1 ? 'document' : 'documents'}`;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id={`${surface.key}-award-profile`}
        panel={tab === 'nominees' ? 'bleed' : 'cards'}
        header={
          <ProfileHeader
            id={`${surface.key}-award-profile-header`}
            icon={<Award className="h-6 w-6" aria-hidden="true" />}
            title={record.categoryName}
            meta={[record.referenceNumber]}
            status={<StatusText tone={record.isActive ? 'active' : 'inactive'}>{record.isActive ? 'Active' : 'Deleted'}</StatusText>}
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            // No Edit here: the Details card's cog owns the one modal, and a record offers it once.
            settings={{
              onHistory: () => setHistoryOpen(true),
              onDelete: writes ? () => setPending('delete') : undefined,
              deletePermission: 'award-nominations.delete',
              deleteDisabledReason: record.isActive ? null : 'This award is already deleted.',
            }}
          />
        }
        banner={
          <NextActionAlert
            error={Boolean(error)}
            id={`${surface.key}-award-banner`}
            icon={
              error || !record.isActive ? <Ban className="h-4 w-4" aria-hidden="true" /> : <Users className="h-4 w-4" aria-hidden="true" />
            }
            title={error ? 'This page may be out of date' : !record.isActive ? 'Deleted' : `${record.nomineeCount} ${record.nomineeCount === 1 ? 'nominee' : 'nominees'}`}
            description={
              error
                ? error.message
                : !record.isActive
                  ? 'This award is hidden from Nomination and Evaluation. Its nominees and their documents are kept.'
                  : writes
                    ? `Each nominee is asked for the ${documents} on the ${record.categoryName} checklist.`
                    : `Each nominee was asked for the ${documents} on the ${record.categoryName} checklist. Open a nominee to read what they submitted.`
            }
            action={
              writes && !record.isActive ? (
                <PermissionGate permission="award-nominations.delete">
                  <Button
                    id="award-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setPending('restore')}
                    disabled={running}
                  >
                    Restore
                  </Button>
                </PermissionGate>
              ) : null
            }
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'criteria',
            label: (
              <>
                Criteria <CountBadge count={record.criteria.length} />
              </>
            ),
          },
          {
            key: 'nominees',
            label: (
              <>
                Nominees <CountBadge count={record.nomineeCount} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
      >
        {tab === 'nominees' ? (
          // Evaluation reads the nominees THROUGH the stage gates; Nomination lists them all.
          surface.key === 'evaluation' ? (
            <NomineeStagesPanel surface={surface} award={record} onChanged={() => load()} />
          ) : (
            <NomineesPanel surface={surface} award={record} onChanged={() => load()} />
          )
        ) : tab === 'criteria' ? (
          /*
            The SCORE SHEET's lines — its own section with its own Edit and write (`PUT …/criteria`),
            disabled for STATE once scoring starts: a moved maximum would reinterpret scores given.
          */
          <ProfileCard
            id={`${surface.key}-award-profile-criteria`}
            title="Criteria"
            count={record.criteria.length}
            onEdit={writes ? () => setPending('criteria') : undefined}
            editPermission="award-nominations.update"
            editDisabledReason={
              !record.isActive
                ? 'This award is deleted. Restore it before changing it.'
                : record.isScoringStarted
                  ? 'Scoring has started, so the criteria are fixed.'
                  : null
            }
            empty={
              record.criteria.length === 0
                ? { title: 'No criteria yet', description: 'Nominees are scored against the criteria, so an award needs at least one.' }
                : undefined
            }
          >
            {record.criteria.length === 0 ? null : (
              <PreviewTable
                variant="table-display"
                rows={[
                  ...record.criteria.map((c) => ({
                    label: c.awardCategoryDocumentId != null ? `${c.name} (document)` : c.name,
                    value: (
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="font-mono">{c.maxPoints} points</span>
                        {c.description ? <span className="text-xs text-slate-400">{c.description}</span> : null}
                      </span>
                    ),
                  })),
                  { label: 'Total', value: `${record.maxTotal} points`, mono: true },
                ]}
              />
            )}
          </ProfileCard>
        ) : (
          <>
            <ProfileCard
              id={`${surface.key}-award-profile-details`}
              title="Details"
              onEdit={writes ? () => setPending('details') : undefined}
              editPermission="award-nominations.update"
              editDisabledReason={record.isActive ? null : 'This award is deleted. Restore it before changing it.'}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Reference number', value: record.referenceNumber, mono: true },
                  { label: 'Category', value: record.categoryName },
                  { label: 'Number of winners', value: String(record.winnerCount), mono: true },
                  { label: 'Description', value: record.description },
                ]}
              />
            </ProfileCard>

            {/*
              A DERIVED VIEW of the CATEGORY's checklist — edited under PRAISE → Settings, so this card
              has no Edit of its own. It is what a nominee registered NOW is asked for.
            */}
            <ProfileCard id={`${surface.key}-award-profile-checklist`} title="Document requirements" count={record.checklist.length}>
              <PreviewTable
                variant="table-display"
                rows={record.checklist.map((document) => ({ label: document.name, value: document.description }))}
              />
            </ProfileCard>

            {/* Audit is never editable: created and updated are a record of what happened. */}
            <ProfileCard id={`${surface.key}-award-profile-audit`} title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: record.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(record.createdAt)} className="font-mono">
                        {formatDateTime(record.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: record.updatedByName },
                  {
                    label: 'Updated',
                    value:
                      record.updatedAt != null ? (
                        <time dateTime={toIso(record.updatedAt)} className="font-mono">
                          {formatDateTime(record.updatedAt)}
                        </time>
                      ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        )}
      </ProfileHeaderShell>

      {pending === 'details' || pending === 'criteria' ? (
        <AwardModal
          open
          section={pending}
          editing={record}
          onClose={() => setPending(null)}
          onSaved={(saved) => {
            setAward(saved);
            setPending(null);
          }}
        />
      ) : null}

      {pending === 'delete' || pending === 'restore' ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setActionError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={pending === 'delete' ? 'Delete award' : 'Restore award'}
          danger={pending === 'delete'}
          requireConfirm
          requireCredential={pending === 'delete'}
          loading={running}
          id={`award-profile-${pending}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {pending === 'delete' ? (
                <>
                  <p>{record.categoryName} is hidden from Nomination and Evaluation.</p>
                  <p>Nothing is erased — its {record.nomineeCount} nominees and their documents stay, and it can be restored.</p>
                </>
              ) : (
                <p>{record.categoryName} returns where it was, with its nominees.</p>
              )}
              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      <HistoryLogsModal open={historyOpen} onClose={() => setHistoryOpen(false)} module={surface.module} entityId={record.id} />
    </div>
  );
}
